/**
 * NarrativeDisplay — Renders structured AI narratives with visual hierarchy
 *
 * Parses plain-text AI narratives into sections with headers, paragraphs,
 * and numbered steps using the shared narrativeParser.
 */

import { hasStructuredContent, parseNarrative } from '@parlay-picker/shared/narrativeParser';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface NarrativeDisplayProps {
  /** Raw AI narrative text */
  narrative: string;
}

// =============================================================================
// Component
// =============================================================================

export function NarrativeDisplay({ narrative }: NarrativeDisplayProps): ReactElement {
  const parsed = parseNarrative(narrative);

  // Plain text fallback — no sections or steps detected
  if (!hasStructuredContent(parsed)) {
    return <p className="text-theme-text-secondary text-sm whitespace-pre-line">{narrative}</p>;
  }

  return (
    <div className="space-y-3">
      {parsed.sections.map((section, sIdx) => (
        <div key={sIdx}>
          {/* Section header with purple left accent bar */}
          {section.title && (
            <div className="flex items-center gap-2 mt-1 mb-1.5">
              <div className="w-0.5 h-3.5 rounded-full bg-purple-500 shrink-0" />
              <span className="text-[10px] font-medium uppercase tracking-wider text-purple-400">
                {section.title}
              </span>
            </div>
          )}

          {/* Paragraphs */}
          {section.paragraphs.map((p, pIdx) => (
            <p key={`p-${pIdx}`} className="text-theme-text-secondary text-sm mb-2">
              {p}
            </p>
          ))}

          {/* Numbered steps */}
          {section.steps.length > 0 && (
            <div className="space-y-2 pl-1">
              {section.steps.map((step) => (
                <div key={`s-${step.number}`} className="flex gap-2">
                  <span className="font-mono text-xs text-purple-400 w-4 shrink-0 text-right pt-0.5">
                    {step.number}
                  </span>
                  <span className="text-theme-text-secondary text-sm">{step.content}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
